import { useEffect, useState } from 'react';
import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';

interface Props {
  position?:
    | 'top-right'
    | 'top-left'
    | 'top-center'
    | 'bottom-right'
    | 'bottom-left'
    | 'bottom-center';
  /** Auto-dismiss duration in ms. Default 4500. */
  duration?: number;
}

function useResolvedTheme(): 'light' | 'dark' {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const root = document.documentElement;
    const sync = (): void => setIsDark(root.classList.contains('dark'));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => mo.disconnect();
  }, []);
  return isDark ? 'dark' : 'light';
}

export function Toaster({
  position = 'top-right',
  duration = 4500,
}: Props = {}): JSX.Element {
  const theme = useResolvedTheme();
  return (
    <SonnerToaster
      theme={theme}
      position={position}
      duration={duration}
      richColors
      closeButton
      toastOptions={{
        classNames: {
          toast: 'rounded-lg border border-border bg-background text-foreground shadow-lg',
          title: 'text-sm font-medium',
          description: 'text-xs text-muted-foreground',
          actionButton: 'text-xs font-medium',
        },
      }}
    />
  );
}

export const toast = {
  success: (title: string, description?: string): string | number =>
    sonnerToast.success(title, description ? { description } : undefined),
  error: (title: string, description?: string): string | number =>
    sonnerToast.error(title, description ? { description } : undefined),
  info: (title: string, description?: string): string | number =>
    sonnerToast.info(title, description ? { description } : undefined),
  warning: (title: string, description?: string): string | number =>
    sonnerToast.warning(title, description ? { description } : undefined),
  message: (title: string, description?: string): string | number =>
    sonnerToast(title, description ? { description } : undefined),
  dismiss: (id?: string | number): void => {
    sonnerToast.dismiss(id);
  },
};
